import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-e4b32e48"),n=n(),_popScopeId(),n)
const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { class: "pa-2" }
const _hoisted_4 = { class: "pa-2" }
const _hoisted_5 = {
  key: 0,
  class: "text-left"
}
const _hoisted_6 = { class: "text-center mb-3" }
const _hoisted_7 = { class: "text-subtitle-1 font-weight-bold mb-1" }
const _hoisted_8 = {
  key: 0,
  class: "text-body-2 text-medium-emphasis mb-2"
}
const _hoisted_9 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("h2", { class: "text-subtitle-1 font-weight-bold mb-1" }, "Jugadas", -1))
const _hoisted_10 = { class: "pa-2" }
const _hoisted_11 = { class: "pa-2" }

import { ref, onMounted, computed, ComputedRef, nextTick, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import CabeceraJugador from "@/components/DetalleJugador/CabeceraJugador.vue";
import ResumenJugador from "@/components/DetalleJugador/ResumenJugador.vue";
import { FiltroPartidas, partidasJugadas } from "@/utils/historialJugador";
import { useAuth } from "@/composables/useAuth";
import {
  ViewPartidaAmistosaDTO,
  ViewPartidaTorneoDTO,
} from "@/interfaces/Partidas";
import { UsuarioDataDTO } from "@/interfaces/Usuario";
import LoadingGandalf from "@/components/Commons/LoadingGandalf.vue";
import { useUsuariosStore } from "@/store/usuarios";
import PartidasJugador from "@/components/DetalleJugador/PartidasJugador.vue";
import { getTournamentMatches } from "@/services/PartidaTorneoService";
import TablaTorneosDisputados from "@/components/DetalleJugador/TablaTorneosDisputados.vue";
import PendingMatchCard from "@/components/PartidaAmistosa/PendingMatchCard.vue";
import ModalRegistrarPartida from "@/components/PartidaAmistosa/ModalRegistrarPartida.vue";
import ModalProteccionDatos from "@/components/Usuarios/ModalProteccionDatos.vue";

type Pestana = (typeof PESTANAS)[number];

export default /*#__PURE__*/_defineComponent({
  __name: 'DetalleJugadorView',
  setup(__props) {

const router = useRouter();
const route = useRoute();

// La pestaña y el filtro van en la URL (?pestana=eventos&rival=12): volver atrás los conserva y se pueden compartir
const PESTANAS = ["resumen", "amistosas", "eventos", "resultados"] as const;
const textoDeQuery = (valor: unknown) => (typeof valor === "string" && valor ? valor : undefined);
const pestanaDeRuta = (): Pestana => {
  const pestana = textoDeQuery(route.query.pestana) as Pestana | undefined;
  return pestana && PESTANAS.includes(pestana) ? pestana : "resumen";
};
const filtroDeRuta = (): FiltroPartidas | null => {
  const idRival = Number(route.query.rival);
  if (Number.isInteger(idRival) && idRival > 0) return { idRival };
  const ejercito = textoDeQuery(route.query.ejercito);
  return ejercito ? { ejercito } : null;
};

const tab = ref<Pestana>(pestanaDeRuta());
const pestanas = ref<HTMLElement>();

// Un ejército o un rival pulsado en el resumen: sus partidas, en eventos o en amistosas, con las pestañas a la vista
const filtroPartidas = ref<FiltroPartidas | null>(filtroDeRuta());
const verPartidasFiltradas = async (filtro: FiltroPartidas, origen: "torneo" | "amistosa") => {
  filtroPartidas.value = filtro;
  tab.value = origen === "amistosa" ? "amistosas" : "eventos";
  await nextTick();
  // Por debajo de la barra de arriba, que es fija
  const arriba = pestanas.value?.getBoundingClientRect().top;
  if (arriba !== undefined)
    window.scrollTo({ top: window.scrollY + arriba - 72, behavior: "smooth" });
};
const usuariosStore = useUsuariosStore();

const isLoading = ref(true);
const isLoadingTournamentMatches = ref<boolean>(false);

watch([tab, filtroPartidas], () => {
  const query: Record<string, string> = {};
  if (tab.value !== "resumen") query.pestana = tab.value;
  if (filtroPartidas.value?.idRival != null) query.rival = String(filtroPartidas.value.idRival);
  else if (filtroPartidas.value?.ejercito) query.ejercito = filtroPartidas.value.ejercito;
  const actual = route.query;
  const igual =
    Object.keys(query).length === Object.keys(actual).length &&
    Object.entries(query).every(([clave, valor]) => actual[clave] === valor);
  if (!igual) router.replace({ query });
});
const usuarioData: ComputedRef<UsuarioDataDTO> = computed(
  () => usuariosStore.usuarioData
);

const pendingMatches = ref<ViewPartidaAmistosaDTO[]>([]);
const validMatches = ref<ViewPartidaAmistosaDTO[]>([]);
const tournamentMatches = ref<ViewPartidaTorneoDTO[]>([]);

// Torneo y amistosas juntas, solo las jugadas: para la cabecera y el resumen
const partidas = computed(() =>
  usuarioData.value?.idUsuario
    ? partidasJugadas(usuarioData.value.idUsuario, tournamentMatches.value, validMatches.value)
    : []
);
// Con el rival en la URL solo viene su id: el nick sale de sus partidas
const filtroConNick = computed<FiltroPartidas | null>(() => {
  const filtro = filtroPartidas.value;
  if (filtro?.idRival == null || filtro.nickRival) return filtro;
  const idRival = filtro.idRival;
  const partida = partidas.value.find(p => p.idsRivales.includes(idRival));
  return { ...filtro, nickRival: partida?.nicksRivales[partida.idsRivales.indexOf(idRival)] || "su rival" };
});
const { getidUsuario } = useAuth();
const idUsuarioQueMira = computed(() =>
  getidUsuario.value ? Number(getidUsuario.value) : null
);

// Tu propia ficha hace de Mis partidas: registrar amistosas y validar las pendientes
const esMiFicha = computed(
  () => !!usuarioData.value?.idUsuario && usuarioData.value.idUsuario === idUsuarioQueMira.value
);
const validadaPorMi = (partida: ViewPartidaAmistosaDTO) =>
  partida.idUsuario1 === idUsuarioQueMira.value
    ? partida.partidaValidadaUsuario1
    : partida.partidaValidadaUsuario2;
// Primero las que te toca validar, luego las que esperan al rival
const pendientes = computed(() =>
  [...pendingMatches.value].sort(
    (a, b) => Number(validadaPorMi(a)) - Number(validadaPorMi(b))
  )
);
const porValidar = computed(() => pendientes.value.filter(p => !validadaPorMi(p)).length);

const registrando = ref(false);
const vecesRegistro = ref(0);
const abrirRegistro = () => {
  vecesRegistro.value++;
  registrando.value = true;
};
const showModalProteccionDatos = ref(false);

const cargarPartidasTorneos = async () => {
  try {
    isLoadingTournamentMatches.value = true;
    const response = await getTournamentMatches(usuarioData.value.idUsuario);

    tournamentMatches.value = response.data;
    tournamentMatches.value = tournamentMatches.value.sort((a, b) => {
      return (
        new Date(b.fechaPartida).getTime() - new Date(a.fechaPartida).getTime()
      );
    });
  } catch (error) {
    console.error("Error al obtener las partidas de torneo:", error);
  } finally {
    isLoadingTournamentMatches.value = false;
  }
};

const initializeComponent = async () => {
  isLoading.value = true;
  try {
    const idRecibido = String(route.params.idUsuario);
    await usuariosStore.requestUsuarioData(parseInt(idRecibido));

    if (usuarioData.value.idUsuario) {
      ordenarAmistosas();
      cargarPartidasTorneos();
      // Como hacía Mis partidas: en tu ficha, si aún no has aceptado la protección de datos
      if (esMiFicha.value && usuarioData.value.proteccionDatos == null)
        showModalProteccionDatos.value = true;
    }
  } catch (error) {
    console.error(error);
    router.push({ name: "error" });
  } finally {
    isLoading.value = false;
  }
};

// Las amistosas jugadas y las pendientes, de la más reciente a la más antigua
function ordenarAmistosas() {
  const reciente = (a: ViewPartidaAmistosaDTO, b: ViewPartidaAmistosaDTO) =>
    new Date(b.fechaPartida).getTime() - new Date(a.fechaPartida).getTime();
  validMatches.value = [...usuarioData.value.partidasValidadas].sort(reciente);
  pendingMatches.value = [...usuarioData.value.partidasPendientes].sort(reciente);
}

// Tras registrar, validar o cancelar una amistosa: otra vez sus partidas, sin recargar toda la ficha
const recargarAmistosas = async () => {
  try {
    await usuariosStore.requestUsuarioData(usuarioData.value.idUsuario);
    ordenarAmistosas();
  } catch (error) {
    console.error(error);
  }
};

onMounted(initializeComponent);

// Ir de un jugador a otro (p. ej. pulsando un nick) reutiliza esta vista: recargar datos
watch(
  () => route.params.idUsuario,
  (nuevoId, anteriorId) => {
    if (nuevoId && nuevoId !== anteriorId) {
      // Lo que diga la URL del otro jugador: sin nada, su resumen; al volver atrás, lo que se estaba viendo
      tab.value = pestanaDeRuta();
      filtroPartidas.value = filtroDeRuta();
      initializeComponent();
    }
  }
);

const goBack = () => {
  router.back();
};

return (_ctx: any,_cache: any) => {
  const _component_v_tab = _resolveComponent("v-tab")!
  const _component_v_tabs = _resolveComponent("v-tabs")!
  const _component_v_window_item = _resolveComponent("v-window-item")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_window = _resolveComponent("v-window")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(_component_v_container, { class: "text-center" }, {
      default: _withCtx(() => [
        _createVNode(_component_v_row, { dense: "" }, {
          default: _withCtx(() => [
            _createVNode(_component_v_col, {
              cols: "12",
              md: "12",
              class: "text-center"
            }, {
              default: _withCtx(() => [
                (isLoading.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                      _createVNode(LoadingGandalf)
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_2, [
                      _createVNode(CabeceraJugador, {
                        usuario: usuarioData.value,
                        partidas: partidas.value
                      }, null, 8, ["usuario", "partidas"]),
                      _createElementVNode("div", {
                        ref_key: "pestanas",
                        ref: pestanas
                      }, [
                        _createVNode(_component_v_tabs, {
                          modelValue: tab.value,
                          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((tab).value = $event)),
                          color: "primary",
                          grow: "",
                          "show-arrows": ""
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_v_tab, { value: "resumen" }, {
                              default: _withCtx(() => [
                                _createTextVNode("Resumen")
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_tab, { value: "amistosas" }, {
                              default: _withCtx(() => [
                                _createTextVNode("Amistosas")
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_tab, { value: "eventos" }, {
                              default: _withCtx(() => [
                                _createTextVNode("Eventos")
                              ]),
                              _: 1
                            }),
                            _createVNode(_component_v_tab, { value: "resultados" }, {
                              default: _withCtx(() => [
                                _createTextVNode("Resultados")
                              ]),
                              _: 1
                            })
                          ]),
                          _: 1
                        }, 8, ["modelValue"])
                      ], 512),
                      _createVNode(_component_v_window, {
                        modelValue: tab.value,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((tab).value = $event))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_v_window_item, { value: "resumen" }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_3, [
                                _createVNode(ResumenJugador, {
                                  usuario: usuarioData.value,
                                  partidas: partidas.value,
                                  idUsuarioQueMira: idUsuarioQueMira.value,
                                  onFiltrar: verPartidasFiltradas
                                }, null, 8, ["usuario", "partidas", "idUsuarioQueMira"])
                              ])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_window_item, { value: "amistosas" }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_4, [
                                (esMiFicha.value)
                                  ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                                      _createElementVNode("div", _hoisted_6, [
                                        _createVNode(_component_v_btn, {
                                          color: "primary",
                                          onClick: abrirRegistro
                                        }, {
                                          default: _withCtx(() => [
                                            _createTextVNode(" Registrar partida ")
                                          ]),
                                          _: 1
                                        })
                                      ]),
                                      (pendientes.value.length > 0)
                                        ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                            _createElementVNode("h2", _hoisted_7, " Pendientes de validar (" + _toDisplayString(pendientes.value.length) + ") ", 1),
                                            (porValidar.value > 0)
                                              ? (_openBlock(), _createElementBlock("p", _hoisted_8, _toDisplayString(porValidar.value === 1 ? "Tienes 1 partida" : `Tienes ${porValidar.value} partidas`) + " esperando tu validación. ", 1))
                                              : _createCommentVNode("", true),
                                            _createVNode(_component_v_row, {
                                              dense: "",
                                              class: "mb-3"
                                            }, {
                                              default: _withCtx(() => [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pendientes.value, (partida) => {
                                                  return (_openBlock(), _createBlock(_component_v_col, {
                                                    key: partida.idPartidaAmistosa,
                                                    cols: "12",
                                                    sm: "6",
                                                    lg: "4"
                                                  }, {
                                                    default: _withCtx(() => [
                                                      _createVNode(PendingMatchCard, {
                                                        match: partida,
                                                        onRegistroExitoso: recargarAmistosas
                                                      }, null, 8, ["match"])
                                                    ]),
                                                    _: 2
                                                  }, 1024))
                                                }), 128))
                                              ]),
                                              _: 1
                                            }),
                                            _hoisted_9
                                          ], 64))
                                        : _createCommentVNode("", true)
                                    ]))
                                  : _createCommentVNode("", true),
                                _createVNode(PartidasJugador, {
                                  partidas: partidas.value,
                                  origen: "amistosa",
                                  idUsuario: usuarioData.value.idUsuario,
                                  filtro: filtroConNick.value,
                                  "onUpdate:filtro": _cache[1] || (_cache[1] = ($event: any) => (filtroPartidas.value = $event))
                                }, null, 8, ["partidas", "idUsuario", "filtro"])
                              ])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_window_item, { value: "eventos" }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_10, [
                                _createVNode(PartidasJugador, {
                                  partidas: partidas.value,
                                  origen: "torneo",
                                  idUsuario: usuarioData.value.idUsuario,
                                  filtro: filtroConNick.value,
                                  "onUpdate:filtro": _cache[2] || (_cache[2] = ($event: any) => (filtroPartidas.value = $event))
                                }, null, 8, ["partidas", "idUsuario", "filtro"])
                              ])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_window_item, { value: "resultados" }, {
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_11, [
                                _createVNode(TablaTorneosDisputados, {
                                  clasificacionTorneos: usuarioData.value.clasificacionTorneos,
                                  tournamentMatches: tournamentMatches.value,
                                  idUsuario: usuarioData.value.idUsuario,
                                  isLoading: isLoadingTournamentMatches.value
                                }, null, 8, ["clasificacionTorneos", "tournamentMatches", "idUsuario", "isLoading"])
                              ])
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["modelValue"])
                    ]))
              ]),
              _: 1
            }),
            _createVNode(_component_v_row, {
              align: "center",
              justify: "center"
            }, {
              default: _withCtx(() => [
                _createVNode(_component_v_col, { cols: "auto" }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_btn, {
                      color: "orange",
                      variant: "tonal",
                      class: "login-form__button mt-4",
                      onClick: goBack
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(" Volver ")
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }),
    (registrando.value)
      ? (_openBlock(), _createBlock(ModalRegistrarPartida, {
          key: vecesRegistro.value,
          onClose: _cache[4] || (_cache[4] = ($event: any) => (registrando.value = false)),
          onRegistroExitoso: recargarAmistosas
        }))
      : _createCommentVNode("", true),
    (showModalProteccionDatos.value)
      ? (_openBlock(), _createBlock(ModalProteccionDatos, {
          key: 1,
          onModalClosed: _cache[5] || (_cache[5] = ($event: any) => (showModalProteccionDatos.value = false))
        }))
      : _createCommentVNode("", true)
  ], 64))
}
}

})